<script setup lang="ts">
const props = defineProps<{
  images: string[]
  initialIndex: number
  clinicName: string
}>()

const emit = defineEmits<{
  close: []
}>()

const r2PublicUrl = useRuntimeConfig().public.r2PublicUrl

const currentIndex = ref(props.initialIndex)
const lightboxRef = ref<HTMLElement | null>(null)

const currentUrl = computed(() => `${r2PublicUrl}/${props.images[currentIndex.value]}`)

function prev() {
  currentIndex.value = currentIndex.value === 0
    ? props.images.length - 1
    : currentIndex.value - 1
}

function next() {
  currentIndex.value = currentIndex.value === props.images.length - 1
    ? 0
    : currentIndex.value + 1
}

onMounted(() => {
  lightboxRef.value?.focus()
})

let touchStartX = 0
let touchEndX = 0

function handleTouchStart(e: TouchEvent) {
  touchStartX = e.changedTouches[0]?.screenX ?? 0
}

function handleTouchEnd(e: TouchEvent) {
  touchEndX = e.changedTouches[0]?.screenX ?? 0
  handleSwipe()
}

function handleSwipe() {
  const swipeThreshold = 50
  if (touchEndX < touchStartX - swipeThreshold) next()
  if (touchEndX > touchStartX + swipeThreshold) prev()
}

onMounted(() => {
  const el = lightboxRef.value
  if (el) {
    el.addEventListener('touchstart', handleTouchStart, { passive: true })
    el.addEventListener('touchend', handleTouchEnd, { passive: true })
  }
})

onUnmounted(() => {
  const el = lightboxRef.value
  if (el) {
    el.removeEventListener('touchstart', handleTouchStart)
    el.removeEventListener('touchend', handleTouchEnd)
  }
})
</script>

<template>
  <Teleport to="body">
    <div
      ref="lightboxRef"
      class="fixed inset-0 z-[100] bg-black overflow-hidden"
      tabindex="0"
      @keydown.escape="emit('close')"
      @keydown.left="prev"
      @keydown.right="next"
      @click="emit('close')"
    >
      <!-- Top bar -->
      <div class="absolute top-0 left-0 right-0 h-14 flex items-center justify-between px-4 z-10">
        <div class="text-white/80 text-sm">
          {{ currentIndex + 1 }} / {{ images.length }}
        </div>
        <UButton
          variant="ghost"
          icon="i-lucide-x"
          size="lg"
          square
          aria-label="Zavřít"
          @click="emit('close')"
        />
      </div>

      <!-- Image area -->
      <div class="absolute top-14 bottom-28 md:bottom-40 left-4 md:left-16 right-4 md:right-16 flex items-center justify-center">
        <img
          :src="currentUrl"
          :alt="`Fotografie kliniky ${clinicName} ${currentIndex + 1}`"
          class="max-w-full max-h-full object-contain"
          @click.stop
        >
      </div>

      <!-- Previous button -->
      <UButton
        v-if="images.length > 1"
        class="absolute left-4 top-1/2 -translate-y-1/2 z-10"
        variant="ghost"
        icon="i-lucide-chevron-left"
        size="lg"
        square
        aria-label="Předchozí fotografie"
        @click.stop="prev"
      />

      <!-- Next button -->
      <UButton
        v-if="images.length > 1"
        class="absolute right-4 top-1/2 -translate-y-1/2 z-10"
        variant="ghost"
        icon="i-lucide-chevron-right"
        size="lg"
        square
        aria-label="Další fotografie"
        @click.stop="next"
      />

      <!-- Bottom bar -->
      <div class="absolute bottom-0 left-0 right-0 h-28 md:h-40 flex flex-col justify-end pb-4 md:pb-6" @click.stop>
        <!-- Thumbnails -->
        <div
          v-if="images.length > 1"
          class="flex justify-center gap-2 max-w-[90vw] mx-auto overflow-hidden px-4"
        >
          <button
            v-for="(key, index) in images"
            :key="key"
            class="w-10 h-12 md:w-12 md:h-16 rounded overflow-hidden flex-shrink-0 border-2 transition-all"
            :class="index === currentIndex ? 'border-white' : 'border-transparent opacity-50 hover:opacity-100'"
            @click="currentIndex = index"
          >
            <img
              :src="`${r2PublicUrl}/${key}`"
              :alt="`Náhled ${index + 1}`"
              class="w-full h-full object-cover"
            >
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
